import { useEffect, useState } from "react";
import { Plus, Trash2, Save, Edit3, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const blank = { name: "", category: "", description: "", cost: 0, tray_price: 0, per_person_price: 0, active: true };

const cls = "w-full bg-[#0a0a0a] border border-white/10 px-3 py-2 text-sm focus:border-[#C9963F] focus:outline-none text-white placeholder-white/30";

export default function MenuManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState(blank);

  async function load() {
    setLoading(true);
    try { const { data } = await api.get("/menu-items"); setItems(data || []); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  function startCreate() { setDraft(blank); setEditingId(null); setCreating(true); }
  function startEdit(it) { setDraft({ ...it }); setEditingId(it.id); setCreating(false); }
  function cancel() { setEditingId(null); setCreating(false); setDraft(blank); }

  async function save() {
    if (!draft.name?.trim()) return toast.error("Name is required");
    const payload = {
      ...draft,
      cost: Number(draft.cost) || 0,
      tray_price: Number(draft.tray_price) || 0,
      per_person_price: Number(draft.per_person_price) || 0,
    };
    try {
      if (editingId) {
        await api.put(`/menu-items/${editingId}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/menu-items", payload);
        toast.success("Added");
      }
      cancel(); load();
    } catch { toast.error("Failed to save"); }
  }

  async function remove(it) {
    if (!window.confirm(`Delete "${it.name}"?`)) return;
    try { await api.delete(`/menu-items/${it.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  }

  async function toggleActive(it) {
    try { await api.put(`/menu-items/${it.id}`, { active: !it.active }); load(); } catch {}
  }

  // Group by category for display
  const grouped = items.reduce((acc, it) => {
    const key = it.category || "Uncategorized";
    (acc[key] = acc[key] || []).push(it);
    return acc;
  }, {});

  // Aggregate margin info
  const totalCost = items.reduce((a, i) => a + (i.cost || 0), 0);
  const totalTrayValue = items.reduce((a, i) => a + (i.tray_price || 0), 0);
  const avgMargin = items.length
    ? Math.round(((totalTrayValue - totalCost) / Math.max(totalTrayValue, 1)) * 100)
    : 0;

  return (
    <div data-testid="menu-manager-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9963F] mb-2">Internal · Not visible to clients</div>
          <h1 className="font-serif-brand text-4xl sm:text-5xl tracking-tight">Catering Menu</h1>
          <p className="text-white/50 mt-2 text-sm">{items.length} items · approx. {avgMargin}% gross margin on trays</p>
        </div>
        <button onClick={startCreate} data-testid="add-menu-item-btn"
          className="inline-flex items-center gap-2 bg-[#C9963F] text-black px-4 py-2 text-sm font-medium hover:bg-[#A67A2D]">
          <Plus size={16} /> Add Item
        </button>
      </div>

      {(creating || editingId) && (
        <div className="bg-[#0e0e0e] border border-[#C9963F]/40 p-6 mb-6 fade-up" data-testid="menu-item-editor">
          <div className="flex items-center justify-between mb-4">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9963F]">{editingId ? "Edit Item" : "New Item"}</div>
            <button onClick={cancel} className="text-white/50 hover:text-white"><X size={18} /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-1">
              <label className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Name *</label>
              <input data-testid="menu-input-name" className={cls} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Category</label>
              <input data-testid="menu-input-category" className={cls} value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })} placeholder="Appetizer, Main, Dessert…" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Description</label>
              <textarea rows={2} className={cls} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Cost ($) — internal</label>
              <input type="number" step="0.01" data-testid="menu-input-cost" className={cls} value={draft.cost} onChange={(e) => setDraft({ ...draft, cost: e.target.value })} />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Tray Price ($)</label>
              <input type="number" step="0.01" data-testid="menu-input-tray" className={cls} value={draft.tray_price} onChange={(e) => setDraft({ ...draft, tray_price: e.target.value })} />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Per Person ($)</label>
              <input type="number" step="0.01" data-testid="menu-input-pp" className={cls} value={draft.per_person_price} onChange={(e) => setDraft({ ...draft, per_person_price: e.target.value })} />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Active</label>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} className="accent-[#C9963F]" />
                <span className="text-sm text-white/80">{draft.active ? "Active" : "Hidden"}</span>
              </label>
            </div>
          </div>
          <div className="flex gap-3 mt-5">
            <button onClick={save} data-testid="save-menu-item-btn" className="bg-[#C9963F] text-black font-medium px-5 py-2 text-sm hover:bg-[#A67A2D] inline-flex items-center gap-2">
              <Save size={14} /> Save
            </button>
            <button onClick={cancel} className="text-white/60 hover:text-white px-4 py-2 text-sm">Cancel</button>
          </div>
        </div>
      )}

      {loading && <div className="text-white/40 text-sm">Loading…</div>}
      {!loading && items.length === 0 && (
        <div className="bg-[#0e0e0e] border border-white/10 p-10 text-center text-white/40 text-sm">
          No menu items yet. Add your first one to get started.
        </div>
      )}

      {Object.entries(grouped).map(([cat, list]) => (
        <div key={cat} className="mb-8" data-testid={`menu-category-${cat}`}>
          <h2 className="text-[10px] uppercase tracking-[0.3em] text-[#C9963F] mb-3">{cat}</h2>
          <div className="bg-[#0e0e0e] border border-white/10">
            <div className="hidden sm:grid grid-cols-12 text-[10px] uppercase tracking-[0.2em] text-white/50 px-5 py-3 border-b border-white/10">
              <div className="col-span-4">Name</div>
              <div className="col-span-3">Description</div>
              <div className="col-span-1 text-right">Cost</div>
              <div className="col-span-1 text-right">Tray</div>
              <div className="col-span-1 text-right">Per Person</div>
              <div className="col-span-1 text-right">Margin</div>
              <div className="col-span-1 text-right">Actions</div>
            </div>
            {list.map((it) => {
              const margin = it.tray_price > 0 ? Math.round(((it.tray_price - it.cost) / it.tray_price) * 100) : 0;
              return (
                <div key={it.id} data-testid={`menu-item-${it.id}`}
                  className={`grid grid-cols-1 sm:grid-cols-12 sm:items-center gap-2 px-5 py-3 border-b border-white/5 ${!it.active ? "opacity-50" : ""}`}>
                  <div className="sm:col-span-4">
                    <div className="text-white text-sm">{it.name}</div>
                    {!it.active && <div className="text-[10px] uppercase tracking-wider text-white/40">Hidden</div>}
                  </div>
                  <div className="sm:col-span-3 text-xs text-white/50 truncate">{it.description || "—"}</div>
                  <div className="sm:col-span-1 sm:text-right text-xs text-white/70">${(it.cost || 0).toFixed(2)}</div>
                  <div className="sm:col-span-1 sm:text-right text-sm text-[#C9963F]">${(it.tray_price || 0).toFixed(2)}</div>
                  <div className="sm:col-span-1 sm:text-right text-sm text-white">${(it.per_person_price || 0).toFixed(2)}</div>
                  <div className="sm:col-span-1 sm:text-right text-xs">
                    <span className={margin >= 50 ? "text-[#C9963F]" : margin >= 30 ? "text-amber-300" : "text-red-300"}>{margin}%</span>
                  </div>
                  <div className="sm:col-span-1 flex sm:justify-end gap-1.5">
                    <button onClick={() => toggleActive(it)} title="Toggle active" className="px-2 py-1 text-[10px] uppercase tracking-wider border border-white/15 text-white/60 hover:border-white/40">{it.active ? "Hide" : "Show"}</button>
                    <button onClick={() => startEdit(it)} title="Edit" data-testid={`edit-menu-${it.id}`} className="p-1.5 text-white/60 hover:text-[#C9963F]"><Edit3 size={14} /></button>
                    <button onClick={() => remove(it)} title="Delete" className="p-1.5 text-white/60 hover:text-red-400"><Trash2 size={14} /></button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
